import type { Permission, Role } from './types';
import { PERMISSIONS } from './types';

/**
 * RBAC 矩阵 —— 唯一事实来源。
 *
 * 前端用它隐藏/禁用 UI（体验层），Worker 用它强制鉴权（安全层）。
 * 任何权限判断都必须走 `hasPermission` / `hasAnyPermission`，
 * 不要在业务代码里手写 `role === 'admin'` 之类的比较。
 */
export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  /** 平台管理员：全部权限，含用户管理与设备注册表写入 */
  admin: PERMISSIONS,

  /** 操作员（飞手）：可查看机队、全量遥测、下发飞行与航线指令；不可改注册表、不可管用户 */
  operator: [
    'fleet.view',
    'fleet.registry.view',
    'fleet.telemetry.view',
    'fleet.telemetry.stream',
    'drone.command.flight',
    'drone.command.mission',
  ],

  /** 维护员：可增删改设备注册表与凭据、执行维护类指令；不可下发飞行指令、不可管用户 */
  maintainer: [
    'fleet.view',
    'fleet.registry.view',
    'fleet.registry.write',
    'fleet.telemetry.view',
    'drone.command.maintenance',
    'audit.view',
  ],

  /** 观察员：只读 */
  viewer: ['fleet.view', 'fleet.registry.view', 'fleet.telemetry.view'],
};

export const ROLES: readonly Role[] = ['admin', 'operator', 'maintainer', 'viewer'];

export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  admin: '平台管理员',
  operator: '操作员',
  maintainer: '维护员',
  viewer: '观察员',
};

export const ROLE_DESCRIPTIONS: Readonly<Record<Role, string>> = {
  admin: '拥有平台全部权限，可管理用户、设备注册表与平台设置。',
  operator: '可查看机队与全量遥测，并下发起飞、降落、返航及航线任务指令。',
  maintainer: '可维护设备注册表、批量配置与凭据轮换，并执行维护类指令。',
  viewer: '仅可查看机队状态与遥测，无法下发任何指令。',
};

export const PERMISSION_LABELS: Readonly<Record<Permission, string>> = {
  'fleet.view': '查看机队总览',
  'fleet.registry.view': '查看设备注册表',
  'fleet.registry.write': '编辑设备注册表',
  'fleet.telemetry.view': '查看遥测数据',
  'fleet.telemetry.stream': '订阅高频遥测',
  'drone.command.flight': '下发飞行指令',
  'drone.command.mission': '下发航线任务指令',
  'drone.command.maintenance': '执行维护类指令',
  'user.view': '查看用户',
  'user.manage': '管理用户',
  'audit.view': '查看审计日志',
  'settings.manage': '管理平台设置',
};

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

/** 列出某角色的全部权限。 */
export function permissionsForRole(role: Role): Permission[] {
  return [...(ROLE_PERMISSIONS[role] ?? [])];
}

/** 单一权限判定。未知角色一律拒绝。 */
export function hasPermission(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  const granted = ROLE_PERMISSIONS[role];
  if (!granted) return false;
  return granted.includes(permission);
}

/** 任一权限命中即通过。 */
export function hasAnyPermission(
  role: Role | null | undefined,
  permissions: readonly Permission[],
): boolean {
  return permissions.some((permission) => hasPermission(role, permission));
}

/** 全部权限命中才通过。 */
export function hasAllPermissions(
  role: Role | null | undefined,
  permissions: readonly Permission[],
): boolean {
  return permissions.every((permission) => hasPermission(role, permission));
}
